<template>
    <div class="tree-select">
        <button type="button" class="tree-choice" @click.prevent="toggle" :disabled="isDisabled">
            <template v-if="htmlTitle">
                <span :class="{placeholder: !selectStatus}" v-html="selectStatus || placeholder"></span>
            </template>
            <template v-else>
                <span :class="{placeholder: !selectStatus}">{{ selectStatus || placeholder }}</span>
            </template>
            <ion-icon name="chevron-down-outline" v-show="!isOpened"></ion-icon>
            <ion-icon name="chevron-up-outline" v-show="isOpened"></ion-icon>
        </button>
        <div class="tree-drop" v-show="isOpened">
            <div class="tree-search" v-if="useSearch">
                <input
                    type="text"
                    v-model="filterString"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    :placeholder="this.actualStrings.searchPlaceholder"
                />
            </div>
            <div class="tree-select-all" v-if="useSelectAll && isMultiple && countVisibleChildren">
                <label :class="{disabled: countVisibleChildren < actualOptions.length}">
                    <input
                        type="checkbox"
                        :checked="allOptionsIsChecked"
                        @input="manageAll"
                        :disabled="countVisibleChildren < actualOptions.length"/>
                    [{{ this.actualStrings.selectAllOptions }}]
                </label>
            </div>
            <ul ref="list" @scroll="onScroll">
                <li v-if="actualOptions <= 0" class="tree-select-empty">
                    {{ this.actualStrings.emptyTitle }}
                </li>
                <li v-for="option in actualOptions" :key="option.value" v-show="option.visible">
                    <select-option
                        :option="option"
                        :name="actualName"
                        :parent-auto-check="parentAutoCheck"
                        :is-multiple="isMultiple"
                        @check="registerCheck"
                        :uid="uid"
                        :only-end-nodes="onlyEndNodes"
                        :html-title="htmlTitle"
                    ></select-option>
                </li>
            </ul>
            <div class="tree-action" v-if="isMultiple">
                <app-button type="primary" @click="submit">{{ this.actualStrings.buttonTitle }}</app-button>
            </div>
        </div>
    </div>
</template>

<script>
    import debounce from "../../helpers/debounce";
    import Option from "./Option";
    import Mark from 'mark.js/dist/mark.es6.min';

    export default {
        components: {
            'select-option': Option
        },
        props: {
            placeholder: {
                type: String,
                default: ''
            },
            name: String,
            id: String,
            options: {
                type: Array,
                default() {
                    return [];
                }
            },
            value: {
                type: [Number, String, Array],
                default() {
                    return [];
                }
            },
            maxSelectedNames: {
                type: Number,
                default: 3
            },
            isMultiple: {
                type: Boolean,
                default: true
            },
            isDisabled: {
                type: Boolean,
                default: false
            },
            parentAutoCheck: {
                type: Boolean,
                default: true
            },
            strings: {
                type: Object,
                default() {
                    return {};
                }
            },
            htmlTitle: {
                type: Boolean,
                default: false
            },
            onlyEndNodes: {
                type: Boolean,
                default: false
            },
            useSearch: {
                type: Boolean,
                default: true
            },
            useSelectAll: {
                type: Boolean,
                default: true
            },
            maxCheckedOptions: {
                type: [Number, Boolean],
                default: false
            }
        },
        data() {
            const [options, selected, optionCount] = this.prepare(copy(this.options));
            this.filter = debounce((options) => {
                this.marker.unmark();
                const filterString = this.filterString.toLowerCase();
                this.countVisibleChildren = this.filterFunc.call(this, options, filterString);

                if (filterString) {
                    this.marker.mark(filterString);
                }
            }, 1000);

            return {
                isOpened: false,
                actualName: this.isMultiple ? this.name + '[]' : this.name,
                filterString: '',
                actualOptions: options,
                selected: selected,
                allOptionsIsChecked: this.checkIfOptionsChecked(options),
                optionCount,
                countVisibleChildren: options.length,
                uid: `f${(~~(Math.random() * 1e8)).toString(16)}`,
                actualStrings: {
                    ...{
                        searchPlaceholder: 'Search...',
                        emptyTitle: 'No options!',
                        selectAllOptions: 'Select All',
                        allSelected: 'All Selected',
                        selectedCount: 'Selected {selected} of {all} items',
                        buttonTitle: 'Ok'
                    },
                    ...this.strings
                }
            };
        },
        mounted() {
            this.marker = new Mark(this.$refs.list);

            this.$globalEvents.$on('select:open', (instance) => {
                if (instance !== this) {
                    this.isOpened = false;
                }
            });
        },
        methods: {
            toggle() {
                this.isOpened = !this.isOpened;

                if (this.isOpened) {
                    this.$globalEvents.$emit('select:open', this);
                }
            },
            setOptionDisabled(option) {
                let countSelected = this.value ? this.value.length : 0;
                if (this.maxCheckedOptions) {
                    return countSelected >= this.maxCheckedOptions && option.checked === false;
                }
                return false
            },
            prepare(options) {
                const selected = {};
                let optionCount = 0;

                options = options.map(option => {
                    option.visible = true;
                    option.checked = option.checked || (this.isMultiple ? (this.value.findIndex(el => option.id == el) > -1) : (this.value == option.id));
                    option.isDisabled = this.setOptionDisabled(option);

                    if (option.checked) {
                        selected[option.id] = option;
                    }

                    if (option.children) {
                        option.isChildrenVisible = false;
                    }

                    optionCount++;

                    if (option.children) {
                        const [preparedChildren, selectedChildren, childrenCount] = this.prepare(option.children);
                        option.children = preparedChildren;
                        Object.assign(selected, selectedChildren);
                        optionCount += childrenCount;
                    }
                    return option;
                });

                return [options, selected, optionCount];
            },
            filterFunc(options, filterString) {
                let visibleOptionsCount = 0;

                options.forEach(option => {
                    let isVisible = option.title.toLowerCase().includes(filterString);

                    if (option.children && option.children.length) {
                        let visibleChildrenCount = this.filterFunc(option.children, filterString);

                        if (visibleChildrenCount) {
                            option.isChildrenVisible = !!filterString;
                            option.isDisabled = visibleChildrenCount < option.children.length;
                        }

                        if (!isVisible) {
                            isVisible = !!visibleChildrenCount;
                        }
                    }

                    option.visible = isVisible;

                    if (isVisible) {
                        visibleOptionsCount++;
                    }
                });

                return visibleOptionsCount;
            },
            change() {
                this.$emit('input', Object.keys(this.selected));
                this.isOpened = false;
            },
            submit() {
                this.$emit('submit', Object.keys(this.selected));
                this.isOpened = false;
            },
            checkIfOptionsChecked(options) {
                return options.every(option => option.checked);
            },
            registerCheck(option, isChecked, isDirectChild) {
                if (isChecked) {
                    option.checked = true;
                    if (this.isMultiple) {
                        if (!(this.onlyEndNodes && option.children && option.children.length)) {
                            this.$set(this.selected, option.id, option);
                            this.$emit('input', Object.keys(this.selected));
                        }
                    } else {
                        const selectedOptionKey = Object.keys(this.selected)[0];

                        if (selectedOptionKey) {
                            this.selected[selectedOptionKey].checked = false;
                        }

                        this.selected = {[option.id]: option};
                        this.change();
                    }
                } else {
                    this.$delete(this.selected, option.id);
                    option.checked = false;
                    this.$emit('input', Object.keys(this.selected));
                }

                this.toggleDuplicateOptions(this.actualOptions, option.id, isChecked);
                if (isDirectChild) {
                    this.allOptionsIsChecked = this.checkIfOptionsChecked(this.actualOptions);
                }
            },
            toggleDuplicateOptions(options, optionId, checked) {
                for (let opt of options) {
                    if (opt.id === optionId) {
                        opt.checked = checked;
                    }
                    if (opt.children) {
                        this.toggleDuplicateOptions(opt.children, optionId, checked);
                    }
                }
            },
            manageAll() {
                this.allOptionsIsChecked = !this.allOptionsIsChecked;
                this.traverseTree(this.actualOptions, (option) => {
                    this.registerCheck(option, this.allOptionsIsChecked, false);
                })
            },
            traverseTree(tree, callback) {
                tree.forEach(option => {
                    callback(option);

                    if (option.children && option.children.length) {
                        this.traverseTree(option.children, callback);
                    }
                });
            },
            updateStateOptions() {
                const [options, selected, optionCount] = this.prepare(copy(this.options));
                this.actualOptions = options;
                this.selected = selected;
                this.optionCount = optionCount;
            },
            onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
                this.$emit('scroll');
                if (scrollTop + clientHeight >= scrollHeight - 50) {
                    this.$emit('bottom');
                }
            },
        },
        computed: {
            selectStatus() {
                const selectedKeys = Object.keys(this.selected);

                if (!selectedKeys.length) {
                    return '';
                } else if (selectedKeys.length < this.maxSelectedNames) {
                    return selectedKeys.map(key => this.selected[key].title).join(', ');
                } else if (selectedKeys.length < this.optionCount) {
                    return this.actualStrings.selectedCount
                        .replace('{selected}', selectedKeys.length)
                        .replace('{all}', this.optionCount);
                } else {
                    return this.actualStrings.allSelected;
                }
            }
        },
        watch: {
            //TODO: we need to watch for new values if they were changed dynamically
            options(newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                    this.updateStateOptions();
                };
            },
            value(newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                    this.updateStateOptions();
                };
            },
            filterString: function () {
                this.$emit('filter-string-changed', this.filterString);
                this.filter(this.actualOptions);
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "resources/sass/_variables";

    .tree-select {
        width: 100%;
        position: relative;

        .tree-choice, .tree-drop {
            padding: 0;
            overflow: hidden;
            border: 1px solid $content-border-color;
            text-align: left;
            border-radius: 4px;
        }

        .tree-choice {
            display: block;
            width: 100%;
            height: 38px;
            cursor: pointer;
            white-space: nowrap;
            line-height: 34px;
            color: #444;
            text-decoration: none;
            background-color: #fff;
            position: relative;

            span {
                position: absolute;
                top: 0;
                left: 0;
                right: 20px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: block;
                padding-left: 12px;
            }

            ion-icon {
                position: absolute;
                top: 0;
                right: 10px;
                width: 10px;
                height: 34px;
            }

            &:focus {
                outline: 1px solid #14a2e2;
            }
        }

        .tree-drop {
            width: 100%;
            display: block;
            top: 100%;
            box-shadow: 0 4px 5px rgba(0,0,0,.15);
            margin-top: -1px;
            position: absolute;
            z-index: 1000;
            background: #fff;
            color: #000;

            .tree-select-empty {
                line-height: 100px;
                text-align: center;
            }

            .tree-search {
                display: inline-block;
                min-height: 26px;
                padding: 4px;
                position: relative;
                white-space: nowrap;
                width: 100%;
                z-index: 10000;

                input {
                    width: 100%;
                    height: auto!important;
                    min-height: 24px;
                    padding: 0 20px 0 5px;
                    margin: 0;
                    outline: 0;
                    font-family: sans-serif;
                    font-size: 1em;
                    border: 1px solid #e5e5e5;
                }
            }

            .tree-select-all {
                margin-left: 16px;
                margin-right: 5px;
                margin-top: 10px;

                label {
                    margin-bottom: 0;
                    margin-right: 5px;
                    width: 100%;
                    padding: 1px 0 1px 5px;
                    display: block;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    cursor: pointer;

                    &:hover {
                        background: $info-background-color;
                        color: $info-text-color;

                        &.disabled {
                            cursor: not-allowed;
                            background: #ccc;
                            color: #000;
                        }
                    }
                }

                input {
                    margin-right: 5px;
                }
            }

            ul {
                margin: 0 4px;
                padding: 5px 0 0;
                overflow-x: hidden;
                overflow-y: auto;
                max-height: 340px;
            }

            .tree-action {
                padding: 4px;

                button {
                    display: block;
                    width: 100%;
                    padding: 4px;
                    border-radius: 4px;
                    font-size: 12px;
                }
            }
        }
    }
</style>
